import {
  DEFAULT_LANGUAGE_DETECTOR_ORDER,
  initializeI18nextInstance,
} from '@linktr.ee/i18n-toolkit/runtime';
import { createInstance, type i18n as I18nInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
  LOCALIZATION_GATE_ID,
  MODE_CONFIG,
  type LocalizationMode,
  type LocaleForMode,
} from './locale-config';
import {
  getCompoundLocale,
  getExplicitExperimentLocaleOverride,
  resolveLocale,
} from './locale-utils';
import { reportClientError } from '../utils/reportClientError';

const DEFAULT_MODE = 'control' as const;
const INTERNAL_LOCALE_SEPARATOR = '__';

const languageDetector = new LanguageDetector(null, {
  order: [...DEFAULT_LANGUAGE_DETECTOR_ORDER],
});
languageDetector.init();

const i18n = createInstance();
const loadedLocaleKeys = new Set<string>([
  `${DEFAULT_MODE}${INTERNAL_LOCALE_SEPARATOR}en`,
]);
let initPromise: Promise<I18nInstance> | undefined;
let latestApplyModeRequestId = 0;

const getDetectedLocaleCandidate = (): string | undefined => {
  const detected = languageDetector.detect();
  return Array.isArray(detected) ? detected[0] : detected;
};

const detectLocale = <M extends LocalizationMode>(mode: M): LocaleForMode<M> =>
  resolveLocale(
    [getDetectedLocaleCandidate()].filter((candidate): candidate is string =>
      Boolean(candidate)
    ),
    MODE_CONFIG[mode].aliasMap,
    MODE_CONFIG[mode].supportedLocales
  ) as LocaleForMode<M>;

const getInternalLocaleKey = <M extends LocalizationMode>(
  mode: M,
  locale: LocaleForMode<M>
): string => `${mode}${INTERNAL_LOCALE_SEPARATOR}${locale}`;

const getPublicLocale = (language: string): string =>
  language.split(INTERNAL_LOCALE_SEPARATOR)[1] ?? 'en';

const missingKeyHandler = (
  lngs: readonly string[],
  ns: string,
  key: string,
  _fallbackValue: string
) => {
  reportClientError('[i18n-toolkit] Missing translation key', {
    key,
    lng: lngs,
    ns,
    source: 'missingKeyHandler',
  });
};

const missingInterpolationHandler = (_text: string, match: string[]) => {
  reportClientError('[i18n-toolkit] Missing i18n interpolation value', {
    source: 'missingInterpolationHandler',
    variable: match[1],
  });

  return match[0] ?? '';
};

export const detectStatsigLocale = (): LocaleForMode<'experiment'> =>
  detectLocale('experiment');

const syncDocumentLanguage = (language: string) => {
  document.documentElement.lang = getCompoundLocale(getPublicLocale(language));
};

i18n.on('languageChanged', syncDocumentLanguage);

const loadLocaleResource = async <M extends LocalizationMode>(
  mode: M,
  locale: LocaleForMode<M>
): Promise<Record<string, unknown>> => {
  if (locale === 'en') {
    return MODE_CONFIG[mode].baseResources.en.translation;
  }

  const loaderMap = MODE_CONFIG[mode].loaders as Partial<
    Record<string, () => Promise<{ default: Record<string, unknown> }>>
  >;
  const loader = loaderMap[locale];

  if (!loader) {
    throw new Error(`Missing locale loader for ${mode}:${locale}`);
  }

  const module = await loader();
  return module.default;
};

const ensureLocaleLoaded = async <M extends LocalizationMode>(
  mode: M,
  locale: LocaleForMode<M>,
  instance: I18nInstance
): Promise<string> => {
  const internalLocaleKey = getInternalLocaleKey(mode, locale);

  if (loadedLocaleKeys.has(internalLocaleKey)) {
    return internalLocaleKey;
  }

  const resource = await loadLocaleResource(mode, locale);
  instance.addResourceBundle(
    internalLocaleKey,
    'translation',
    resource,
    true,
    true
  );
  loadedLocaleKeys.add(internalLocaleKey);

  return internalLocaleKey;
};

export const initializeI18n = (): Promise<I18nInstance> => {
  if (initPromise) {
    return initPromise;
  }

  initPromise = initializeI18nextInstance(i18n, {
    resources: {
      [getInternalLocaleKey(DEFAULT_MODE, 'en')]:
        MODE_CONFIG.control.baseResources.en,
    },
    lng: getInternalLocaleKey(DEFAULT_MODE, 'en'),
    fallbackLng: getInternalLocaleKey(DEFAULT_MODE, 'en'),
    missingInterpolationHandler,
    missingKeyHandler,
    interpolation: {
      escapeValue: false,
    },
  }).then(instance => {
    syncDocumentLanguage(instance.language);
    return instance;
  });

  return initPromise;
};

export const applyLocalizationMode = async <M extends LocalizationMode>(
  mode: M,
  explicitLocale?: LocaleForMode<M>
): Promise<void> => {
  const requestId = ++latestApplyModeRequestId;
  const locale = explicitLocale ?? detectLocale(mode);
  const instance = await initializeI18n();
  const internalLocaleKey = await ensureLocaleLoaded(mode, locale, instance);

  if (requestId !== latestApplyModeRequestId) {
    return;
  }

  if (instance.language !== internalLocaleKey) {
    await instance.changeLanguage(internalLocaleKey);
  }

  syncDocumentLanguage(instance.language);
};

void initializeI18n().catch(error => {
  reportClientError('Failed to initialize base i18n', error, {
    source: 'i18n-initialization',
  });
});

export {
  LOCALIZATION_GATE_ID,
  getCompoundLocale,
  getExplicitExperimentLocaleOverride,
};
export type { LocalizationMode };
export default i18n;
